import React, { useEffect, useState } from "react";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Range } from "react-date-range";
import { useTranslation } from "react-i18next";

import useTimezoneDate from "../../../../hooks/useTimezoneDate";
import { DashboardPeriodOptions } from "../../../../util/enums";
import { Button, Input } from "../../../Form";
import { Color } from "../../../Form/Button";
import InputDateRange from "../../../Form/Input/DateRange";
import { useSupplyChainOverviewContext } from "../context";
import { useStyles } from "./styles";

interface Props {
  opened: boolean;
  setOpened: (value: boolean) => void;
}

const CustomPeriodModal = ({ opened, setOpened }: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { getDateValueUtcTimezone } = useTimezoneDate();
  const { customDate, customPeriod, selectedPeriod, setCustomDate, setCustomPeriod } =
    useSupplyChainOverviewContext();

  const [date, setDate] = useState<Date>();
  const [from, setFrom] = useState<Date>();
  const [to, setTo] = useState<Date>();

  useEffect(() => setDate(customDate), [customDate]);

  useEffect(() => {
    setFrom(customPeriod?.from);
    setTo(customPeriod?.to);
  }, [customPeriod]);

  const onApply = () => {
    if (date) setCustomDate(date);
    if (from && to) setCustomPeriod({ from, to });
    onCancel();
  };

  const onCancel = () => {
    setOpened(false);
  };

  const renderContent = () => {
    if (selectedPeriod === DashboardPeriodOptions.CUSTOM_DATE) {
      return (
        <Input
          data-cy="input-custom-date"
          label={translate("date")}
          name="custom-date"
          onChange={(event) => setDate(new Date(event.target.value))}
          type="date"
          value={getDateValueUtcTimezone(date?.toISOString())}
        />
      );
    }
    if (selectedPeriod === DashboardPeriodOptions.CUSTOM_PERIOD) {
      return (
        <InputDateRange
          data-cy="input-custom-period"
          label={translate("period")}
          name="custom-period"
          onChange={(range: Range) => {
            setFrom(range?.startDate);
            setTo(range?.endDate);
          }}
          pEndDate={to}
          pStartDate={from}
        />
      );
    }
  };

  const disabledApply = () => {
    if (selectedPeriod === DashboardPeriodOptions.CUSTOM_DATE) return !date;
    if (selectedPeriod === DashboardPeriodOptions.CUSTOM_PERIOD) return !from && !to;
  };

  const rightButtons = (
    <>
      <Button customColor={Color.CANCEL} data-cy="button-cancel" onClick={onCancel} variant="text">
        {translate("cancel")}
      </Button>
      <Button
        customColor={Color.SUCCESS}
        data-cy="button-apply"
        disabled={disabledApply()}
        onClick={onApply}
      >
        {translate("apply")}
      </Button>
    </>
  );

  return (
    <Dialog className={classes.dialog} fullWidth maxWidth="xs" onClose={onCancel} open={opened}>
      <DialogTitle className={classes.title}>
        {translate(selectedPeriod)}
        <IconButton aria-label="close" className={classes.closeButton} onClick={onCancel}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.content}>{renderContent()}</DialogContent>
      <Divider />
      <DialogActions className={classes.actions}>{rightButtons}</DialogActions>
    </Dialog>
  );
};

export default CustomPeriodModal;
